import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createEventSchema,
  updateEventSchema,
  type EventDetailDto,
  type EventRule,
  type TeamScoring,
} from '@rw/shared';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Page } from '../../components/Layout';
import { ErrorState, LoadingState } from '../../components/states';
import { Button, Card } from '../../components/ui';
import { invalidateEvents, useEventDetail, useEventList, useRegion } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { toast } from '../../stores/toasts';
import { RULE_EXPLAIN, RULE_LABEL, SCORING_LABEL, toLocalInput } from './event-format';
import { useT } from '../../i18n';
import { saveZaloBot } from './zalo-api';
import { SponsorsEditor, type SponsorPick } from './SponsorsEditor';
import { useAuth } from '../../stores/auth';

const input = 'h-11 w-full rounded-xl border border-ink-700 bg-ink-900 px-3 text-sm outline-none focus:border-volt disabled:opacity-50';

/** "5:30" → 330 s; empty → null. */
function parsePace(v: string): number | null | 'bad' {
  if (!v.trim()) return null;
  const m = /^(\d{1,2}):([0-5]\d)$/.exec(v.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : 'bad';
}
const numOrNull = (v: string, scale = 1) => (v.trim() ? Number(v) * scale : null);
const km = (m: number | null) => (m == null ? '' : String(Math.round(m) / 1000));
const pace = (sec: number | null) => (sec == null ? '' : `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`);
const num = (n: number | null) => (n == null ? '' : String(n));

type Form = {
  name: string;
  description: string;
  rule: EventRule;
  teamScoring: TeamScoring;
  visibility: 'PUBLIC' | 'PRIVATE';
  startsAt: string;
  endsAt: string;
  minRunKm: string;
  maxRunKm: string;
  fastestPace: string;
  slowestPace: string;
  minTotalKm: string;
  minRuns: string;
  minSectors: string;
  maxParticipants: string;
};

/** Form fields that become read-only once the event is live (mirrors the API's LOCKED_AFTER_START). */
const LOCKED_LIVE: Array<keyof Form> = ['rule', 'teamScoring', 'startsAt', 'minRunKm', 'maxRunKm', 'fastestPace', 'slowestPace', 'minTotalKm', 'minRuns', 'minSectors'];

function blankForm(): Form {
  const now = Date.now();
  return {
    name: '',
    description: '',
    rule: 'DISTANCE',
    teamScoring: 'TOTAL',
    visibility: 'PUBLIC',
    startsAt: toLocalInput(new Date(now + 3_600_000)),
    endsAt: toLocalInput(new Date(now + 14 * 86_400_000)),
    minRunKm: '',
    maxRunKm: '',
    fastestPace: '',
    slowestPace: '',
    minTotalKm: '',
    minRuns: '',
    minSectors: '',
    maxParticipants: '',
  };
}

function formFrom(e: EventDetailDto): Form {
  const r = e.rules;
  return {
    name: e.name,
    description: e.description,
    rule: e.rule,
    teamScoring: e.teamScoring,
    visibility: e.visibility,
    startsAt: toLocalInput(new Date(e.startsAt)),
    endsAt: toLocalInput(new Date(e.endsAt)),
    minRunKm: km(r.minRunDistanceM),
    maxRunKm: km(r.maxRunDistanceM),
    fastestPace: pace(r.minPaceSecPerKm),
    slowestPace: pace(r.maxPaceSecPerKm),
    minTotalKm: km(r.minTotalDistanceM),
    minRuns: num(r.minRuns),
    minSectors: num(r.minSectors),
    maxParticipants: num(r.maxParticipants),
  };
}

/** Form field → API field(s) it produces. */
const API_FIELDS: Record<keyof Form, string[]> = {
  name: ['name'],
  description: ['description'],
  rule: ['rule'],
  teamScoring: ['teamScoring'],
  visibility: ['visibility'],
  startsAt: ['startsAt'],
  endsAt: ['endsAt'],
  minRunKm: ['minRunDistanceM'],
  maxRunKm: ['maxRunDistanceM'],
  fastestPace: ['minPaceSecPerKm'],
  slowestPace: ['maxPaceSecPerKm'],
  minTotalKm: ['minTotalDistanceM'],
  minRuns: ['minRuns'],
  minSectors: ['minSectors'],
  maxParticipants: ['maxParticipants'],
};

export function CreateEventPage() {
  const list = useEventList();
  const navigate = useNavigate();
  const t = useT();
  if (list.isPending) return <Page><LoadingState /></Page>;
  if (list.data && !list.data.canCreate)
    return (
      <Page>
        <div data-testid="event-creation-closed">
          <Card className="mt-6 text-center">
            <div className="text-3xl">🏁</div>
            <p className="mt-2 font-semibold">{t('Only Runner Wars can create events right now')}</p>
            <p className="mt-1 text-sm text-ink-400">{t('You can still join events and organise the ones you are an organiser of.')}</p>
            <Button className="mt-4 w-full" variant="secondary" onClick={() => navigate('/events')}>
              {t('See events')}
            </Button>
          </Card>
        </div>
      </Page>
    );
  return <EventForm initial={blankForm()} needsApproval={!!list.data?.createNeedsApproval} freeEvents={list.data?.freeEventsPerRunner} />;
}

export function EditEventPage() {
  const { id } = useParams();
  const q = useEventDetail(id);
  if (q.isPending) return <Page><LoadingState /></Page>;
  if (!q.data) return <Page><ErrorState error={q.error} onRetry={() => q.refetch()} /></Page>;
  // Remount with the loaded event so the form starts from its current values.
  return <EventForm key={q.data.id} initial={formFrom(q.data)} event={q.data} />;
}

function EventForm({ initial, event, needsApproval, freeEvents }: { initial: Form; event?: EventDetailDto; needsApproval?: boolean; freeEvents?: number }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const editing = !!event;
  const live = event?.status === 'ACTIVE';
  const [f, setF] = useState<Form>(initial);
  const [error, setError] = useState<string | null>(null);
  const [zaloToken, setZaloToken] = useState('');
  const t = useT();
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const locked = (k: keyof Form) => live && LOCKED_LIVE.includes(k);
  // The cities whose runs count: picked, or every city (incl. cities opened later).
  const { data: regions, region: current } = useRegion();
  const initialCities = event
    ? { all: event.allRegions, ids: event.regionIds.length ? event.regionIds : [event.regionId] }
    : { all: false, ids: current ? [current.id] : [] };
  // null until the runner picks: a new event defaults to the city being shown (regions may load after the first render).
  const [pickedCities, setCities] = useState<{ all: boolean; ids: string[] } | null>(null);
  const cities = pickedCities ?? initialCities;
  // Sponsors: the game owner picks them for any event.
  const isOwner = useAuth((s) => s.user?.role === 'SUPER_ADMIN');
  const initialSponsors: SponsorPick[] = (event?.sponsors ?? []).map((s) => ({ brandId: s.brand.id, tier: s.tier, note: s.note }));
  const [sponsors, setSponsors] = useState<SponsorPick[]>(initialSponsors);
  const sponsorsChanged = JSON.stringify(sponsors) !== JSON.stringify(initialSponsors);
  const citiesChanged = cities.all !== initialCities.all || [...cities.ids].sort().join() !== [...initialCities.ids].sort().join();

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown> | null) => {
      const ev = !body
        ? event!
        : editing
          ? await api<EventDetailDto>(`/competitions/${event.id}`, { method: 'PATCH', body })
          : await api<EventDetailDto>('/competitions', { body });
      if (!isOwner || !sponsorsChanged) return ev;
      const saved = await api<EventDetailDto['sponsors']>(`/admin/events/${ev.id}/sponsors`, { method: 'PUT', body: { sponsors } });
      return { ...ev, sponsors: saved };
    },
    onSuccess: async (ev) => {
      invalidateEvents(qc);
      if (!editing && zaloToken.trim()) {
        // The event exists either way; a bad token can be fixed from the event page.
        await saveZaloBot(ev.id, zaloToken.trim()).catch((e: Error) =>
          toast({ kind: 'error', title: t('Zalo bot not saved'), body: e instanceof ApiError ? e.message : undefined }, 6000),
        );
      }
      if (editing) {
        qc.setQueryData(['event', ev.id], ev);
        toast({ kind: 'success', title: t('Event updated') });
      } else if (ev.approval === 'PENDING') {
        toast({ kind: 'info', title: t('Sent for approval'), body: t("You'll be notified when Runner Wars approves it.") }, 6000);
      } else {
        toast({ kind: 'success', title: t('Event created'), body: t('Add teams and invite runners.') });
      }
      navigate(`/events/${ev.id}`, { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : t(editing ? 'Could not save the event' : 'Could not create the event')),
  });

  const submit = () => {
    const fast = parsePace(f.fastestPace);
    const slow = parsePace(f.slowestPace);
    if (fast === 'bad' || slow === 'bad') return setError(t('Pace must look like 5:30'));
    const body = {
      name: f.name,
      description: f.description,
      rule: f.rule,
      teamScoring: f.teamScoring,
      visibility: f.visibility,
      startsAt: new Date(f.startsAt),
      endsAt: new Date(f.endsAt),
      minRunDistanceM: numOrNull(f.minRunKm, 1000),
      maxRunDistanceM: numOrNull(f.maxRunKm, 1000),
      minPaceSecPerKm: fast,
      maxPaceSecPerKm: slow,
      minTotalDistanceM: numOrNull(f.minTotalKm, 1000),
      minRuns: numOrNull(f.minRuns),
      minSectors: numOrNull(f.minSectors),
      maxParticipants: numOrNull(f.maxParticipants),
      ...(cities.all ? { allRegions: true } : { allRegions: false, regionIds: cities.ids }),
    };
    if (!cities.all && cities.ids.length === 0) return setError(t('Pick at least one city'));
    const parsed = createEventSchema.safeParse(body);
    if (!parsed.success) {
      const i = parsed.error.issues[0];
      return setError(i ? `${i.path.length ? `${i.path.join('.')}: ` : ''}${t(i.message)}` : t('Invalid event'));
    }
    setError(null);
    if (!editing) return save.mutate(parsed.data);
    // Send only what changed (a live event refuses changes to its rules).
    const changed: Record<string, unknown> = {};
    for (const k of Object.keys(f) as Array<keyof Form>) {
      if (f[k] === initial[k]) continue;
      for (const field of API_FIELDS[k]) changed[field] = (parsed.data as Record<string, unknown>)[field] ?? null;
    }
    if (citiesChanged) Object.assign(changed, cities.all ? { allRegions: true } : { allRegions: false, regionIds: cities.ids });
    if (Object.keys(changed).length === 0) return sponsorsChanged ? save.mutate(null) : navigate(`/events/${event.id}`, { replace: true });
    const patch = updateEventSchema.safeParse(changed);
    if (!patch.success) return setError(t(patch.error.issues[0]?.message ?? 'Invalid event'));
    save.mutate(patch.data as Record<string, unknown>);
  };

  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        {t('← Back')}
      </button>
      <h1 className="font-display text-4xl font-extrabold uppercase">{t(editing ? 'Edit event' : 'New event')}</h1>
      <p className="mt-1 text-sm text-ink-400">
        {t(
          !editing
            ? "You'll be the organiser. Every event has its own map, so it never clashes with other events."
            : live
              ? 'The event is live: how to win, the start and the run rules are locked. You can still change the name, description, end date and limits.'
              : 'Everything can change until the event starts. Runners who already joined see the new rules right away.',
        )}
      </p>

      <Card className="mt-4 space-y-3">
        <Field label="Name">
          <input value={f.name} onChange={set('name')} maxLength={80} className={input} data-testid="event-name-input" />
        </Field>
        <Field label="Description (optional)">
          <textarea value={f.description} onChange={set('description')} maxLength={1000} rows={3} className={`${input} h-auto py-2`} />
        </Field>
        <Field label="How to win">
          <select value={f.rule} onChange={set('rule')} disabled={locked('rule')} className={input} data-testid="event-rule-input">
            {(Object.keys(RULE_LABEL) as EventRule[]).map((r) => (
              <option key={r} value={r}>
                {t(RULE_LABEL[r])}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-400">{t(RULE_EXPLAIN[f.rule])}</p>
        </Field>
        <Field label="Team score">
          <select value={f.teamScoring} onChange={set('teamScoring')} disabled={locked('teamScoring')} className={input} data-testid="event-scoring-input">
            {(Object.keys(SCORING_LABEL) as TeamScoring[]).map((r) => (
              <option key={r} value={r}>
                {t(SCORING_LABEL[r])}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-400">
            {t(f.teamScoring === 'TOTAL' ? 'Big teams have an edge: every member adds to the team.' : 'Fair for small teams: the team scores its members’ average.')}
          </p>
        </Field>
        {regions && regions.length > 1 ? (
          <div>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-ink-400">{t('Cities')}</span>
            <div className="space-y-2" data-testid="event-cities">
              <CityOption
                label={t('All cities (incl. cities opened later)')}
                checked={cities.all}
                disabled={live}
                onChange={(all) => setCities({ ...cities, all })}
                testId="event-city-all"
              />
              {!cities.all
                ? regions.map((r) => (
                    <CityOption
                      key={r.id}
                      label={r.isPublic ? r.name : `${r.name} · ${t('Owner only')}`}
                      checked={cities.ids.includes(r.id)}
                      disabled={live}
                      onChange={(on) => setCities({ ...cities, ids: on ? [...cities.ids, r.id] : cities.ids.filter((x) => x !== r.id) })}
                      testId={`event-city-${r.id}`}
                    />
                  ))
                : null}
            </div>
            <p className="mt-1 text-xs text-ink-400">{t('Only runs in these cities count. Each city has its own sectors on the event map.')}</p>
          </div>
        ) : null}
        {isOwner ? <SponsorsEditor value={sponsors} onChange={setSponsors} /> : null}
        <Field label="Who can join">
          <select value={f.visibility} onChange={set('visibility')} className={input} data-testid="event-visibility-input">
            <option value="PUBLIC">{t('Public — anyone can find and join')}</option>
            <option value="PRIVATE">{t('Private — invite or link only')}</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts">
            <input type="datetime-local" value={f.startsAt} onChange={set('startsAt')} disabled={locked('startsAt')} className={input} data-testid="event-start-input" />
          </Field>
          <Field label="Ends">
            <input type="datetime-local" value={f.endsAt} onChange={set('endsAt')} className={input} data-testid="event-end-input" />
          </Field>
        </div>
      </Card>

      <div className="mb-2 mt-6 text-xs font-bold uppercase tracking-widest text-ink-400">{t('Which runs count')}</div>
      <Card className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Min km / run">
            <input inputMode="decimal" placeholder="—" value={f.minRunKm} onChange={set('minRunKm')} disabled={locked('minRunKm')} className={input} data-testid="event-min-run" />
          </Field>
          <Field label="Max km / run">
            <input inputMode="decimal" placeholder="—" value={f.maxRunKm} onChange={set('maxRunKm')} disabled={locked('maxRunKm')} className={input} />
          </Field>
          <Field label="Fastest pace">
            <input placeholder={t('e.g. {v}', { v: '4:00' })} value={f.fastestPace} onChange={set('fastestPace')} disabled={locked('fastestPace')} className={input} data-testid="event-fastest-pace" />
          </Field>
          <Field label="Slowest pace">
            <input placeholder={t('e.g. {v}', { v: '15:00' })} value={f.slowestPace} onChange={set('slowestPace')} disabled={locked('slowestPace')} className={input} />
          </Field>
        </div>
        <p className="text-xs text-ink-500">{t('Only validated runs started after a runner joins are counted. Leave blank for no limit.')}</p>
      </Card>

      <div className="mb-2 mt-6 text-xs font-bold uppercase tracking-widest text-ink-400">{t('To finish (and win awards)')}</div>
      <Card className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Total km">
            <input inputMode="decimal" placeholder="—" value={f.minTotalKm} onChange={set('minTotalKm')} disabled={locked('minTotalKm')} className={input} />
          </Field>
          <Field label="Runs">
            <input inputMode="numeric" placeholder="—" value={f.minRuns} onChange={set('minRuns')} disabled={locked('minRuns')} className={input} />
          </Field>
          <Field label="Sectors">
            <input inputMode="numeric" placeholder="—" value={f.minSectors} onChange={set('minSectors')} disabled={locked('minSectors')} className={input} />
          </Field>
        </div>
        <Field label="Max participants">
          <input inputMode="numeric" placeholder={t('Unlimited')} value={f.maxParticipants} onChange={set('maxParticipants')} className={input} />
        </Field>
      </Card>

      {editing ? null : (
        <Card className="mt-4 space-y-2">
          <Field label="Zalo bot token (optional)">
            <input
              value={zaloToken}
              onChange={(e) => setZaloToken(e.target.value)}
              placeholder={t('Bot token from Zalo Bot Creator')}
              autoComplete="off"
              spellCheck={false}
              className={input}
              data-testid="event-zalo-token"
            />
          </Field>
          <p className="text-xs text-ink-500">{t('The bot announces this event in your runners’ Zalo group. You can also add it later from the event page.')}</p>
        </Card>
      )}

      {error ? (
        <p className="mt-4 text-sm text-enemy" data-testid="event-form-error">
          {error}
        </p>
      ) : null}
      {needsApproval && !editing ? (
        <div className="mt-4 rounded-xl border border-gold/30 bg-gold/10 px-3 py-2 text-sm" data-testid="event-needs-approval">
          <div className="font-semibold text-gold">{t('You have used your {n} free events', { n: freeEvents ?? 3 })}</div>
          <div className="mt-0.5 text-xs text-ink-300">{t('This one will be sent to Runner Wars for approval. Only you can see it until it is approved.')}</div>
        </div>
      ) : null}
      <Button className="mt-4 w-full" onClick={submit} loading={save.isPending} data-testid="submit-event">
        {t(editing ? 'Save changes' : needsApproval ? 'Send for approval' : 'Create event')}
      </Button>
    </Page>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  const t = useT();
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-ink-400">{t(label)}</span>
      {children}
    </label>
  );
}

function CityOption({ label, checked, disabled, onChange, testId }: { label: string; checked: boolean; disabled?: boolean; onChange: (on: boolean) => void; testId: string }) {
  return (
    <label className="flex items-center gap-3 text-sm normal-case">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="size-5 accent-volt" data-testid={testId} />
      <span>{label}</span>
    </label>
  );
}
